// external
import type { ForwardRefRenderFunction } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useState, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { camelCase, kebabCase } from 'lodash';

// interstate components
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

// internal components
import { MileageFormSection } from './MileageFormSection';

// hooks
import { useFieldOverrideIndicator } from '../hooks/useFieldOverrideIndicator';

// styles
import { MileageSlideOutContainer } from './MileageCostOptions.style';

// types
import type {
    MileageErrors,
    MileageData,
    MileageCostOptionsTabContentRef,
    MileageCostOptionsTabContentProps
} from './mileageCostOptionsTypes';

// constants
import { MILEAGE_FIELD_LABELS, MILEAGE_SECTION_TITLES, FIELD_TYPE } from './constants';

// utils
import { formatDecimalPlaces } from '../utils/formatUtils';

// validation
import {
    validateMilesAtInception,
    validateThresholdMiles,
    validateRetroMiles,
    validateInceptionRate,
    validateStandardAllowedMiles,
    validateAnnualMiles,
    validateExcessMileRate,
    validateMileagePenaltyRate,
    hasValidationErrors
} from './mileageValidation';

// selectors
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { parseSelectedTermMonths } from '../terms/common/utils';
import * as leaseContractingFieldsActionCreators from '@makemydeal/dr-dash-store/actions/leaseContractingFieldsActionCreators';

const MILEAGE_PENALTY_RATE_OVERRIDE_INDICATOR_TEST_ID = 'mileage-penalty-rate-override-indicator';

const overrideIndicatorTestId = (label: string) => `${kebabCase(label)}-override-indicator`;

const MileageCostOptionsTabContentComponent: ForwardRefRenderFunction<
    MileageCostOptionsTabContentRef,
    MileageCostOptionsTabContentProps
> = ({ onCanApplyChange }, ref) => {
    const dispatch = useDispatch();
    const theme = useInterstateTheme();

    // Mileage selectors
    const isMileageOptionsUIUpdateEnabled = useSelector(featureToggleSelectors.isMileageOptionsUIUpdateEnabled);
    const annualMiles = useSelector(offerReduxSelectors.getAnnualMiles);
    const selectedTermId = useSelector(offerReduxSelectors.getTermIdByOfferType);
    const vehicleDetails = useSelector(offerReduxSelectors.getVehicleDetails);
    const milesAtInception = vehicleDetails?.mileage || 0;
    const totalMiles = useSelector(offerReduxSelectors.getTotalMiles);
    const excessMileageChargeOverride = useSelector(offerReduxSelectors.getMileageChargeOverride);
    const excessMileageCharge = useSelector(offerReduxSelectors.getExcessMileageCharge);
    const mileagePenaltyRate = excessMileageChargeOverride ?? excessMileageCharge;
    const initialMilesCharged = useSelector(offerReduxSelectors.getInitialMilesCharged);
    const initialMileageCharge = useSelector(offerReduxSelectors.getInitialMileageCharge);
    const totalExcessMilesPurchased = useSelector(offerReduxSelectors.getTotalExcessMilesPurchased);
    const excessAmount = useSelector(offerReduxSelectors.getExcessAmount);
    const thresholdMiles = useSelector(offerReduxSelectors.getThresholdMiles);
    const retroMiles = useSelector(offerReduxSelectors.getRetroMiles);
    const inceptionRate = useSelector(offerReduxSelectors.getInceptionRate);
    const standardAllowedMiles = useSelector(offerReduxSelectors.getStandardAllowedMiles);
    const excessMileRate = useSelector(offerReduxSelectors.getExcessMileRate);
    const isAutoSelectUnchecked = useSelector(offerReduxSelectors.getIsUserProgramQuotes);
    // Program-quotes has no override slot for these fields, so an edit there is dropped without an error.
    // The ASP payment grid redesign moves such an edit to user-program-quotes (`pqSupportedOverrides`),
    // so the fields stay editable under it; the same selector gates that routing, keeping the two in step.
    const isAspPaymentGridRedesignActive = useSelector(offerReduxSelectors.isAspPaymentGridRedesignActive);
    const isProgramMileageFieldDisabled = !isAutoSelectUnchecked && !isAspPaymentGridRedesignActive;
    // Mileage Penalty Rate holds the excess mileage charge, so it shares the $/Mi indicator key.
    const mileageChargeIndicator = useFieldOverrideIndicator('mileageCharge');
    const thresholdMilesIndicator = useFieldOverrideIndicator('thresholdMiles');
    const retroMilesIndicator = useFieldOverrideIndicator('retroMiles');
    const inceptionRateIndicator = useFieldOverrideIndicator('inceptionRate');
    const standardAllowedMilesIndicator = useFieldOverrideIndicator('standardAllowedMiles');
    const excessMileRateIndicator = useFieldOverrideIndicator('excessMileRate');

    const terms = useMemo(() => parseSelectedTermMonths(selectedTermId), [selectedTermId]);

    const originalMileageData: MileageData = useMemo(
        () => ({
            milesAtInception,
            thresholdMiles,
            retroMiles,
            inceptionRate,
            standardAllowedMiles,
            initialMilesCharged,
            initialMileageCharge,
            totalExcessMilesPurchased,
            totalMiles,
            excessAmount,
            annualMiles,
            excessMileRate,
            mileagePenaltyRate
        }),
        [
            milesAtInception,
            thresholdMiles,
            retroMiles,
            inceptionRate,
            standardAllowedMiles,
            initialMilesCharged,
            initialMileageCharge,
            totalExcessMilesPurchased,
            totalMiles,
            excessAmount,
            annualMiles,
            excessMileRate,
            mileagePenaltyRate
        ]
    );

    const [currentMileageData, setCurrentMileageData] = useState<MileageData>(originalMileageData);

    // Sync with selector changes
    useEffect(() => {
        setCurrentMileageData(originalMileageData);
    }, [originalMileageData]);

    // Calculate derived mileage values
    const calculatedInitialMilesCharged = useMemo(() => {
        const { milesAtInception: miles, thresholdMiles: threshold, retroMiles: retro } = currentMileageData;

        if (miles === undefined || threshold === undefined || retro === undefined) {
            return undefined;
        }

        if (miles > threshold && miles > retro) {
            return miles - retro;
        }

        return 0;
    }, [currentMileageData.milesAtInception, currentMileageData.thresholdMiles, currentMileageData.retroMiles]);

    const calculatedInitialMileageCharge = useMemo(() => {
        if (calculatedInitialMilesCharged === undefined || currentMileageData.inceptionRate === undefined) {
            return undefined;
        }
        return Math.round(calculatedInitialMilesCharged * currentMileageData.inceptionRate * 100) / 100;
    }, [calculatedInitialMilesCharged, currentMileageData.inceptionRate]);

    const { calculatedTotalExcessMilesPurchased, calculatedTotalMiles } = useMemo(() => {
        const { annualMiles: annual, standardAllowedMiles: standard } = currentMileageData;

        if (terms === undefined || annual === undefined || standard === undefined) {
            return { calculatedTotalExcessMilesPurchased: undefined, calculatedTotalMiles: undefined };
        }

        const total = (terms / 12) * annual;

        if (annual >= standard) {
            const excessMiles = (terms / 12) * (annual - standard);
            return { calculatedTotalExcessMilesPurchased: excessMiles, calculatedTotalMiles: total };
        }

        return { calculatedTotalExcessMilesPurchased: 0, calculatedTotalMiles: total };
    }, [terms, currentMileageData.annualMiles, currentMileageData.standardAllowedMiles]);

    const calculatedExcessAmount = useMemo(() => {
        const { annualMiles: annual, standardAllowedMiles: standard, excessMileRate: rate } = currentMileageData;

        if (
            calculatedTotalExcessMilesPurchased === undefined ||
            annual === undefined ||
            standard === undefined ||
            rate === undefined
        ) {
            return undefined;
        }

        if (annual >= standard) {
            return calculatedTotalExcessMilesPurchased * rate;
        }

        return 0;
    }, [
        calculatedTotalExcessMilesPurchased,
        currentMileageData.annualMiles,
        currentMileageData.standardAllowedMiles,
        currentMileageData.excessMileRate
    ]);

    // Validation
    const mileageErrors: MileageErrors = useMemo(() => {
        const newErrors: MileageErrors = {};

        const milesAtInceptionResult = validateMilesAtInception(currentMileageData.milesAtInception);
        if (!milesAtInceptionResult.isValid) {
            newErrors.milesAtInception = milesAtInceptionResult.error;
        }

        const thresholdMilesResult = validateThresholdMiles(currentMileageData.thresholdMiles ?? null);
        if (!thresholdMilesResult.isValid) {
            newErrors.thresholdMiles = thresholdMilesResult.error;
        }

        const retroMilesResult = validateRetroMiles(currentMileageData.retroMiles ?? null);
        if (!retroMilesResult.isValid) {
            newErrors.retroMiles = retroMilesResult.error;
        }

        const inceptionRateResult = validateInceptionRate(currentMileageData.inceptionRate ?? null);
        if (!inceptionRateResult.isValid) {
            newErrors.inceptionRate = inceptionRateResult.error;
        }

        const standardAllowedMilesResult = validateStandardAllowedMiles(currentMileageData.standardAllowedMiles ?? null);
        if (!standardAllowedMilesResult.isValid) {
            newErrors.standardAllowedMiles = standardAllowedMilesResult.error;
        }

        const annualMilesResult = validateAnnualMiles(currentMileageData.annualMiles ?? null);
        if (!annualMilesResult.isValid) {
            newErrors.annualMiles = annualMilesResult.error;
        }

        const excessMileRateResult = validateExcessMileRate(currentMileageData.excessMileRate ?? null);
        if (!excessMileRateResult.isValid) {
            newErrors.excessMileRate = excessMileRateResult.error;
        }

        const mileagePenaltyRateResult = validateMileagePenaltyRate(currentMileageData.mileagePenaltyRate ?? null);
        if (!mileagePenaltyRateResult.isValid) {
            newErrors.mileagePenaltyRate = mileagePenaltyRateResult.error;
        }

        return newErrors;
    }, [currentMileageData]);

    // Compute derived state for canApply
    const hasChanges = useMemo(() => {
        return JSON.stringify(currentMileageData) !== JSON.stringify(originalMileageData);
    }, [currentMileageData, originalMileageData]);

    const changedFields = useMemo(() => {
        return Object.keys(currentMileageData).filter(
            (key) => currentMileageData[key as keyof MileageData] !== originalMileageData[key as keyof MileageData]
        );
    }, [currentMileageData, originalMileageData]);

    // Expose methods to parent via ref
    useImperativeHandle(
        ref,
        () => ({
            canApply: () => hasChanges && !hasValidationErrors(mileageErrors),
            apply: () => {
                const onlyMileagePenaltyRateChanged = changedFields.length === 1 && changedFields[0] === 'mileagePenaltyRate';

                // Compared as displayed, not raw: the field shows two decimals, so tabbing through it re-sends the
                // rounded value and would otherwise record an edit the user never made.
                const penaltyRateEdited =
                    formatDecimalPlaces(currentMileageData.mileagePenaltyRate) !==
                    formatDecimalPlaces(originalMileageData.mileagePenaltyRate);

                const payload = {
                    ...currentMileageData,
                    initialMilesCharged: calculatedInitialMilesCharged,
                    initialMileageCharge: calculatedInitialMileageCharge,
                    totalExcessMilesPurchased: calculatedTotalExcessMilesPurchased,
                    totalMiles: calculatedTotalMiles,
                    excessAmount: calculatedExcessAmount,
                    mileageChargeOverride: currentMileageData.mileagePenaltyRate,
                    meta: {
                        paymentCall: !onlyMileagePenaltyRateChanged,
                        changedFields
                    }
                };

                dispatch(leaseContractingFieldsActionCreators.requestLeaseContractFields(payload));

                // DE766084: recorded after the apply, not before. An apply that also moves Annual Mileage is
                // program-invalidating, and that clears every recorded override - so a $/Mi recorded first was
                // wiped by the same click that entered it.
                if (penaltyRateEdited) {
                    mileageChargeIndicator.recordManualOverride();
                }

                const asDisplayed = (value: number | undefined | null) => (value == null ? undefined : formatDecimalPlaces(value));
                const isEdited = (key: keyof MileageData) =>
                    asDisplayed(currentMileageData[key]) !== asDisplayed(originalMileageData[key]);

                if (isEdited('thresholdMiles')) thresholdMilesIndicator.recordManualOverride();
                if (isEdited('retroMiles')) retroMilesIndicator.recordManualOverride();
                if (isEdited('inceptionRate')) inceptionRateIndicator.recordManualOverride();
                if (isEdited('standardAllowedMiles')) standardAllowedMilesIndicator.recordManualOverride();
                if (isEdited('excessMileRate')) excessMileRateIndicator.recordManualOverride();
            }
        }),
        [
            hasChanges,
            mileageErrors,
            changedFields,
            currentMileageData,
            originalMileageData,
            mileageChargeIndicator.recordManualOverride,
            thresholdMilesIndicator.recordManualOverride,
            retroMilesIndicator.recordManualOverride,
            inceptionRateIndicator.recordManualOverride,
            standardAllowedMilesIndicator.recordManualOverride,
            excessMileRateIndicator.recordManualOverride,
            calculatedInitialMilesCharged,
            calculatedInitialMileageCharge,
            calculatedTotalExcessMilesPurchased,
            calculatedTotalMiles,
            calculatedExcessAmount,
            dispatch
        ]
    );

    // Notify parent of canApply state changes
    const canApply = hasChanges && !hasValidationErrors(mileageErrors);
    useEffect(() => {
        onCanApplyChange(canApply);
    }, [canApply, onCanApplyChange]);

    const handleMileageFieldChange = useCallback((key: string, value: string | number | undefined) => {
        let newValue: string | number | undefined = value !== '' ? value : undefined;

        if (typeof newValue === 'string') {
            const parsed = Number(newValue);
            if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
                newValue = parsed;
            }
        }

        setCurrentMileageData((prev) => ({ ...prev, [key]: newValue }));
    }, []);

    return (
        <MileageSlideOutContainer>
            {/* Initial Miles Section */}
            <MileageFormSection
                title={MILEAGE_SECTION_TITLES.INITIAL_MILES}
                fields={[
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.MILES_AT_INCEPTION),
                        label: MILEAGE_FIELD_LABELS.MILES_AT_INCEPTION,
                        type: FIELD_TYPE.NUMERIC,
                        value: currentMileageData.milesAtInception,
                        error: mileageErrors.milesAtInception,
                        onChange: handleMileageFieldChange,
                        required: true
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.THRESHOLD_MILES),
                        label: MILEAGE_FIELD_LABELS.CHARGE_WHEN_OVER,
                        type: FIELD_TYPE.NUMERIC,
                        value: currentMileageData.thresholdMiles,
                        error: mileageErrors.thresholdMiles,
                        onChange: handleMileageFieldChange,
                        required: true,
                        tooltip: {
                            content: 'Charges begin when the odometer exceeds this value.',
                            icon: (
                                <InformationCircleIcon
                                    color={theme.tokens.BaseColorBlue500}
                                    data-testid={`${kebabCase(MILEAGE_FIELD_LABELS.CHARGE_WHEN_OVER)}-info-icon`}
                                />
                            )
                        },
                        disabled: isProgramMileageFieldDisabled,
                        overrideIndicator: {
                            ...thresholdMilesIndicator,
                            testId: overrideIndicatorTestId(MILEAGE_FIELD_LABELS.CHARGE_WHEN_OVER)
                        }
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.RETRO_MILES),
                        label: MILEAGE_FIELD_LABELS.CHARGE_BACK_TO,
                        type: FIELD_TYPE.NUMERIC,
                        value: currentMileageData.retroMiles,
                        error: mileageErrors.retroMiles,
                        onChange: handleMileageFieldChange,
                        required: true,
                        tooltip: {
                            content: 'Charges apply only to miles above this value.',
                            icon: (
                                <InformationCircleIcon
                                    color={theme.tokens.BaseColorBlue500}
                                    data-testid={`${kebabCase(MILEAGE_FIELD_LABELS.CHARGE_BACK_TO)}-info-icon`}
                                />
                            )
                        },
                        disabled: isProgramMileageFieldDisabled,
                        overrideIndicator: {
                            ...retroMilesIndicator,
                            testId: overrideIndicatorTestId(MILEAGE_FIELD_LABELS.CHARGE_BACK_TO)
                        }
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.INCEPTION_RATE),
                        label: MILEAGE_FIELD_LABELS.INCEPTION_RATE,
                        type: FIELD_TYPE.CURRENCY,
                        allowDecimal: true,
                        value: formatDecimalPlaces(currentMileageData.inceptionRate),
                        error: mileageErrors.inceptionRate,
                        onChange: handleMileageFieldChange,
                        required: true,
                        disabled: isProgramMileageFieldDisabled,
                        overrideIndicator: {
                            ...inceptionRateIndicator,
                            testId: overrideIndicatorTestId(MILEAGE_FIELD_LABELS.INCEPTION_RATE)
                        }
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.INITIAL_MILES_CHARGED),
                        label: MILEAGE_FIELD_LABELS.INITIAL_MILES_CHARGED,
                        type: FIELD_TYPE.NUMERIC,
                        value: calculatedInitialMilesCharged,
                        readOnly: true
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.INITIAL_MILEAGE_CHARGE),
                        label: MILEAGE_FIELD_LABELS.INITIAL_MILEAGE_CHARGE,
                        type: FIELD_TYPE.CURRENCY,
                        allowDecimal: true,
                        value: formatDecimalPlaces(calculatedInitialMileageCharge),
                        readOnly: true
                    }
                ]}
            />

            {/* Annual Miles Section */}
            <MileageFormSection
                title={MILEAGE_SECTION_TITLES.ANNUAL_MILES}
                fields={[
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.STANDARD_ALLOWED_MILES),
                        label: MILEAGE_FIELD_LABELS.STANDARD_ALLOWED_MILES,
                        type: isMileageOptionsUIUpdateEnabled ? FIELD_TYPE.DROPDOWN : FIELD_TYPE.NUMERIC,
                        value: currentMileageData.standardAllowedMiles,
                        error: mileageErrors.standardAllowedMiles,
                        onChange: handleMileageFieldChange,
                        required: true,
                        tooltip: {
                            content: 'This is the yearly, allotted miles from the lease program.',
                            icon: (
                                <InformationCircleIcon
                                    color={theme.tokens.BaseColorBlue500}
                                    data-testid={`${kebabCase(MILEAGE_FIELD_LABELS.STANDARD_ALLOWED_MILES)}-info-icon`}
                                />
                            )
                        }
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.ANNUAL_MILES),
                        label: MILEAGE_FIELD_LABELS.ANNUAL_MILES,
                        type: isMileageOptionsUIUpdateEnabled ? FIELD_TYPE.NUMERIC : FIELD_TYPE.DROPDOWN,
                        value: currentMileageData.annualMiles,
                        error: mileageErrors.annualMiles,
                        onChange: handleMileageFieldChange,
                        required: true,
                        tooltip: {
                            content: 'This is the total, expected miles which can include additional, purchased miles.',
                            icon: (
                                <InformationCircleIcon
                                    color={theme.tokens.BaseColorBlue500}
                                    data-testid={`${kebabCase(MILEAGE_FIELD_LABELS.ANNUAL_MILES)}-info-icon`}
                                />
                            )
                        }
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.EXCESS_MILE_RATE),
                        label: MILEAGE_FIELD_LABELS.EXCESS_MILE_RATE,
                        type: FIELD_TYPE.CURRENCY,
                        value: formatDecimalPlaces(currentMileageData.excessMileRate),
                        error: mileageErrors.excessMileRate,
                        allowDecimal: true,
                        onChange: handleMileageFieldChange,
                        required: true,
                        disabled: isProgramMileageFieldDisabled,
                        overrideIndicator: {
                            ...excessMileRateIndicator,
                            testId: overrideIndicatorTestId(MILEAGE_FIELD_LABELS.EXCESS_MILE_RATE)
                        }
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.TOTAL_EXCESS_MILES_PURCHASED),
                        label: MILEAGE_FIELD_LABELS.TOTAL_EXCESS_MILES_PURCHASED,
                        type: FIELD_TYPE.NUMERIC,
                        value: calculatedTotalExcessMilesPurchased,
                        readOnly: true
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.TERM_MONTHLY),
                        label: MILEAGE_FIELD_LABELS.TERM_MONTHLY,
                        type: FIELD_TYPE.NUMERIC,
                        value: terms,
                        readOnly: true
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.TOTAL_MILES),
                        label: MILEAGE_FIELD_LABELS.TOTAL_MILES,
                        type: FIELD_TYPE.NUMERIC,
                        value: calculatedTotalMiles,
                        readOnly: true
                    },
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.EXCESS_AMOUNT),
                        label: MILEAGE_FIELD_LABELS.EXCESS_AMOUNT,
                        type: FIELD_TYPE.CURRENCY,
                        value: formatDecimalPlaces(calculatedExcessAmount),
                        allowDecimal: true,
                        readOnly: true
                    }
                ]}
            />

            {/* Penalty Miles Section */}
            <MileageFormSection
                title={MILEAGE_SECTION_TITLES.PENALTY_MILES}
                fields={[
                    {
                        key: camelCase(MILEAGE_FIELD_LABELS.MILEAGE_PENALTY_RATE),
                        label: MILEAGE_FIELD_LABELS.MILEAGE_PENALTY_RATE,
                        type: FIELD_TYPE.CURRENCY,
                        value: formatDecimalPlaces(currentMileageData.mileagePenaltyRate),
                        error: mileageErrors.mileagePenaltyRate,
                        onChange: handleMileageFieldChange,
                        allowDecimal: true,
                        required: true,
                        disabled: isProgramMileageFieldDisabled,
                        overrideIndicator: {
                            ...mileageChargeIndicator,
                            testId: MILEAGE_PENALTY_RATE_OVERRIDE_INDICATOR_TEST_ID
                        }
                    }
                ]}
            />
        </MileageSlideOutContainer>
    );
};

export const MileageCostOptionsTabContent = forwardRef(MileageCostOptionsTabContentComponent);

export default MileageCostOptionsTabContent;
